import React, { useState } from 'react';
import { Box, Text } from '@chakra-ui/react';
import { PlayIcon } from 'lucide-react';
interface LazyVideoProps {
  src: string;
  /** Lightweight poster image. Omit to use the CSS ember placeholder. */
  poster?: string;
  /** CSS aspect-ratio string, e.g. '16 / 9' or '9 / 16'. */
  aspectRatio?: string;
  label: string;
  onError?: () => void;
}

/**
 * Click-to-load video facade.
 *
 * Nothing from the video file is requested until the visitor taps play, so the
 * page ships zero video bytes on first load. Once mounted the element uses
 * preload="metadata" and only plays because the visitor asked for it.
 */
export function LazyVideo({
  src,
  poster,
  aspectRatio = '16 / 9',
  label,
  onError
}: LazyVideoProps) {
  const [activated, setActivated] = useState(false);
  if (activated) {
    return <video controls autoPlay playsInline preload="metadata" poster={poster} onError={onError} style={{
      width: '100%',
      height: 'auto',
      aspectRatio,
      display: 'block',
      background: '#000'
    }} src={src} data-id="element-8" data-mp="2lswc1p7gg4ps">
        Tu navegador no soporta el elemento de video.
      </video>;
  }
  return <Box as="button" type="button" onClick={() => setActivated(true)} aria-label={label} role="group" position="relative" display="block" w="full" bg="#0d0806" bgImage={poster ? `url(${poster})` : undefined} bgSize="cover" bgPosition="center" overflow="hidden" sx={{
    aspectRatio
  }} _focusVisible={{
    outline: '2px solid',
    outlineColor: '#f2a93b',
    outlineOffset: '2px'
  }} data-id="element-9" data-mp="2j5dp153cobji">
      {!poster && <Box aria-hidden="true" position="absolute" inset={0} bgGradient="radial(circle at 50% 78%, rgba(232,99,26,0.32), rgba(10,10,10,0.96) 62%)" data-id="element-10" data-mp="3uk0pmvu511na" />}
      <Box aria-hidden="true" position="absolute" inset={0} bg="rgba(10, 10, 10, 0.3)" data-id="element-11" data-mp="1nwa18puvmg83" />

      <Box position="absolute" top="50%" left="50%" transform="translate3d(-50%, -50%, 0)" w={{
      base: '58px',
      md: '68px'
    }} h={{
      base: '58px',
      md: '68px'
    }} borderRadius="full" bg="rgba(10, 10, 10, 0.6)" borderWidth="2px" borderColor="gold.400" display="flex" alignItems="center" justifyContent="center" boxShadow="0 0 26px rgba(242, 169, 59, 0.4)" transition="transform 0.18s ease" _groupHover={{
      transform: 'translate3d(-50%, -50%, 0) scale(1.07)'
    }} data-id="element-12" data-mp="1rwk9k9655iq4">
        <Box as={PlayIcon} boxSize={{
        base: 6,
        md: 7
      }} color="gold.400" ml="3px" data-id="element-13" data-mp="2plnch8i7g1rg" />
      </Box>

      <Text position="absolute" bottom={3} left={0} right={0} fontSize="xs" color="gray.300" fontWeight="semibold" data-id="element-14" data-mp="2ui49ytbansh7">
        Toca para reproducir
      </Text>
    </Box>;
}